import { Injectable } from '@angular/core';
import { SearchOptions } from '../models/e621.models';

/**
 * Service responsible for date-related search logic: tag building,
 * label formatting, date shifting, and manual tag detection / removal.
 * Extracted from SearchComponent to reduce component complexity.
 */
@Injectable({
  providedIn: 'root',
})
export class SearchDateService {

  // ─── Manual Tag Detection ──────────────────────────────────────────────────

  /** True when the query already contains a manual date: tag. */
  hasManualDateTag(query: string): boolean {
    return /\bdate:/i.test((query || '').trim());
  }

  /** True when the query already contains a manual order: tag. */
  hasManualOrderTag(query: string): boolean {
    return /\border:/i.test((query || '').trim());
  }

  /** True when the query already contains a manual score: tag. */
  hasManualScoreTag(query: string): boolean {
    return /\bscore:/i.test((query || '').trim());
  }

  // ─── Manual Tag Removal ────────────────────────────────────────────────────

  /** Remove any manual date: tags from a query string. */
  removeManualDateTags(query: string): string {
    return (query || '').replace(/\bdate:\S+/gi, '').replace(/\s{2,}/g, ' ').trim();
  }

  /** Remove any manual order: tags from a query string. */
  removeManualOrderTags(query: string): string {
    return (query || '').replace(/\border:\S+/gi, '').replace(/\s{2,}/g, ' ').trim();
  }

  /** Remove any manual score: tags from a query string. */
  removeManualScoreTags(query: string): string {
    return (query || '').replace(/\bscore:\S+/gi, '').replace(/\s{2,}/g, ' ').trim();
  }

  // ─── Date Navigation ──────────────────────────────────────────────────────

  /**
   * Whether the current date selection can be shifted "later" (closer to the
   * present).  Returns false when the selection already covers today.
   */
  canShiftLater(options: SearchOptions): boolean {
    const mode = options.advancedDateMode || 'none';
    if (mode === 'none') return false;

    const now = new Date();

    switch (mode) {
      case 'period':
        return (Number(options.periodOffset) || 0) > 0;

      case 'exact': {
        const granularity = options.exactGranularity || 'day';
        const year  = Number(options.exactYear);
        const month = Number(options.exactMonth);
        const day   = Number(options.exactDay);

        switch (granularity) {
          case 'year':  return year < now.getFullYear();
          case 'month': return year < now.getFullYear() ||
                               (year === now.getFullYear() && month < now.getMonth() + 1);
          case 'day': {
            const sel   = new Date(year, month - 1, day);
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            return sel.getTime() < today.getTime();
          }
          default: return true;
        }
      }

      case 'range': {
        const granularity = options.rangeGranularity || 'month';
        const toYear  = Number(options.rangeToYear);
        const toMonth = Number(options.rangeToMonth);
        const toDay   = Number(options.rangeToDay);

        switch (granularity) {
          case 'year':  return toYear < now.getFullYear();
          case 'month': return toYear < now.getFullYear() ||
                               (toYear === now.getFullYear() && toMonth < now.getMonth() + 1);
          case 'day': {
            const to    = new Date(toYear, toMonth - 1, toDay);
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            return to.getTime() < today.getTime();
          }
          default: return true;
        }
      }

      default:
        return true;
    }
  }

  /**
   * Compute new search-option values after shifting the date period one step.
   * Returns a partial SearchOptions object suitable for patching the form.
   */
  shiftDatePeriod(direction: 'prev' | 'next', options: SearchOptions): Partial<SearchOptions> {
    const mode = options.advancedDateMode || 'none';
    if (mode === 'none') return {};

    switch (mode) {
      case 'period': {
        const current = Number(options.periodOffset) || 0;
        return { periodOffset: direction === 'prev' ? current + 1 : Math.max(0, current - 1) };
      }

      case 'exact': {
        const granularity = options.exactGranularity || 'day';
        const year  = Number(options.exactYear);
        const month = Number(options.exactMonth);
        const day   = Number(options.exactDay);
        const delta = direction === 'prev' ? -1 : 1;

        switch (granularity) {
          case 'year':
            return { exactYear: year + delta };
          case 'month': {
            let nm = month + delta;
            let ny = year;
            if (nm < 1)  { nm += 12; ny--; }
            if (nm > 12) { nm -= 12; ny++; }
            return { exactYear: ny, exactMonth: nm };
          }
          case 'day': {
            const d = new Date(year, month - 1, day);
            d.setDate(d.getDate() + delta);
            return {
              exactYear: d.getFullYear(),
              exactMonth: d.getMonth() + 1,
              exactDay: d.getDate(),
            };
          }
          default:
            return {};
        }
      }

      case 'range': {
        const granularity  = options.rangeGranularity || 'month';
        const fromYear  = Number(options.rangeFromYear);
        const fromMonth = Number(options.rangeFromMonth);
        const fromDay   = Number(options.rangeFromDay);
        const toYear    = Number(options.rangeToYear);
        const toMonth   = Number(options.rangeToMonth);
        const toDay     = Number(options.rangeToDay);

        const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
        const toDate   = new Date(toYear,   toMonth - 1,   toDay);
        const durationMs = Math.abs(toDate.getTime() - fromDate.getTime());
        const sign = direction === 'prev' ? -1 : 1;

        let newFrom: Date;
        let newTo:   Date;

        if (granularity === 'year') {
          const yearSpan = Math.max(1, toYear - fromYear + 1);
          newFrom = new Date(fromDate);
          newFrom.setFullYear(fromDate.getFullYear() + sign * yearSpan);
          newTo = new Date(toDate);
          newTo.setFullYear(toDate.getFullYear() + sign * yearSpan);
        } else if (granularity === 'month') {
          const monthSpan = Math.max(1,
            (toYear - fromYear) * 12 + (toMonth - fromMonth) + 1);
          const shiftFrom = new Date(fromDate);
          shiftFrom.setMonth(shiftFrom.getMonth() + sign * monthSpan);
          const shiftTo = new Date(toDate);
          shiftTo.setMonth(shiftTo.getMonth() + sign * monthSpan);
          newFrom = shiftFrom;
          newTo   = shiftTo;
        } else {
          const shiftMs = sign * durationMs;
          newFrom = new Date(fromDate.getTime() + shiftMs);
          newTo   = new Date(toDate.getTime() + shiftMs);
        }

        return {
          rangeFromYear:  newFrom.getFullYear(),
          rangeFromMonth: newFrom.getMonth() + 1,
          rangeFromDay:   newFrom.getDate(),
          rangeToYear:    newTo.getFullYear(),
          rangeToMonth:   newTo.getMonth() + 1,
          rangeToDay:     newTo.getDate(),
        };
      }

      default:
        return {};
    }
  }

  // ─── Date Labels ───────────────────────────────────────────────────────────

  /** Human-readable label for the current exact-date selection. */
  getExactDateLabel(granularity: string, year: number, month: number, day: number): string {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    switch (granularity) {
      case 'year':  return `${year}`;
      case 'month': return `${monthNames[month - 1]} ${year}`;
      case 'day':   return `${monthNames[month - 1]} ${day}, ${year}`;
      default:      return '';
    }
  }

  /** Human-readable label for the current period offset. */
  getPeriodOffsetLabel(unit: string, offset: number): string {
    if (offset === 0) {
      const labels: Record<string, string> = {
        today: 'Today', day: 'Last 24h', week: 'Last 7 days',
        month: 'Last 30 days', year: 'Last 365 days',
      };
      return labels[unit] ?? unit;
    }
    switch (unit) {
      case 'today':
      case 'day':
        if (offset === 1) return 'Yesterday';
        return `${offset} days ago`;
      case 'week':
        return offset === 1 ? 'Last week' : `${offset} weeks ago`;
      case 'month':
        return offset === 1 ? 'Last month' : `${offset} months ago`;
      case 'year':
        return offset === 1 ? 'Last year' : `${offset} years ago`;
      default:
        return `Offset: ${offset}`;
    }
  }

  /** Human-readable label for the current range selection. */
  getRangeDateLabel(
    granularity: string,
    fromYear: number, fromMonth: number, fromDay: number,
    toYear: number,   toMonth: number,   toDay: number,
  ): string {
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    switch (granularity) {
      case 'year':  return `${fromYear} – ${toYear}`;
      case 'month': return `${m[fromMonth-1]} ${fromYear} – ${m[toMonth-1]} ${toYear}`;
      case 'day':   return `${m[fromMonth-1]} ${fromDay}, ${fromYear} – ${m[toMonth-1]} ${toDay}, ${toYear}`;
      default:      return '';
    }
  }

  // ─── Default Options ───────────────────────────────────────────────────────

  /** Return the default search-option values for the date controls. */
  getDefaultDateOptions(): SearchOptions {
    const now = new Date();
    return {
      advancedDateMode: 'none',
      periodUnit: 'week',
      periodOffset: 0,
      exactGranularity: 'day',
      exactYear: now.getFullYear(),
      exactMonth: now.getMonth() + 1,
      exactDay: now.getDate(),
      rangeGranularity: 'month',
      rangeFromYear: now.getFullYear() - 1,
      rangeFromMonth: now.getMonth() + 1,
      rangeFromDay: now.getDate(),
      rangeToYear: now.getFullYear(),
      rangeToMonth: now.getMonth() + 1,
      rangeToDay: now.getDate(),
      orderBy: '',
      minScore: null,
      maxScore: null,
    };
  }

  // ─── Date Tag Builders ─────────────────────────────────────────────────────

  /**
   * Build the e621 date tag from the given search options.
   */
  buildAdvancedDateTag(options: SearchOptions): string {
    const mode = options.advancedDateMode;
    if (!mode || mode === 'none') return '';

    switch (mode) {
      case 'period': {
        const unit   = options.periodUnit   || 'week';
        const offset = Number(options.periodOffset) || 0;
        return this.buildPeriodDateTag(unit, offset);
      }
      case 'exact': {
        const granularity = options.exactGranularity || 'day';
        const year  = Number(options.exactYear);
        const month = Number(options.exactMonth);
        const day   = Number(options.exactDay);
        return this.buildExactDateTag(granularity, year, month, day);
      }
      case 'range': {
        const granularity  = options.rangeGranularity  || 'month';
        const fromYear  = Number(options.rangeFromYear);
        const fromMonth = Number(options.rangeFromMonth);
        const fromDay   = Number(options.rangeFromDay);
        const toYear    = Number(options.rangeToYear);
        const toMonth   = Number(options.rangeToMonth);
        const toDay     = Number(options.rangeToDay);
        return this.buildRangeDateTag(granularity, fromYear, fromMonth, fromDay, toYear, toMonth, toDay);
      }
      default:
        return '';
    }
  }

  /**
   * Build a date tag for "period" mode.
   * offset=0 → current rolling window; offset=N → N windows further back.
   */
  buildPeriodDateTag(unit: string, offset: number): string {
    if (offset === 0) {
      const map: Record<string, string> = {
        today: 'date:today', day: 'date:day',
        week: 'date:week', month: 'date:month', year: 'date:year',
      };
      return map[unit] ?? '';
    }

    // offset ≥ 1: use ago-range syntax or exact date
    switch (unit) {
      case 'today':
      case 'day': {
        if (offset === 1) return 'date:yesterday';
        // Specific calendar day
        const d = new Date();
        d.setDate(d.getDate() - offset);
        return `date:${this.formatDateStr(d)}`;
      }
      case 'week':
        return `date:${offset + 1}_weeks_ago..${offset}_weeks_ago`;
      case 'month':
        return `date:${offset + 1}_months_ago..${offset}_months_ago`;
      case 'year':
        return `date:${offset + 1}_years_ago..${offset}_years_ago`;
      default:
        return '';
    }
  }

  /**
   * Build a date tag for "exact" mode with year/month/day granularity.
   */
  buildExactDateTag(granularity: string, year: number, month: number, day: number): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    switch (granularity) {
      case 'year':
        return `date:${year}-01-01..${year}-12-31`;
      case 'month': {
        const lastDay = new Date(year, month, 0).getDate();
        return `date:${year}-${pad(month)}-01..${year}-${pad(month)}-${pad(lastDay)}`;
      }
      case 'day':
        return `date:${year}-${pad(month)}-${pad(day)}`;
      default:
        return '';
    }
  }

  /**
   * Build a date tag for "range" mode.
   */
  buildRangeDateTag(
    granularity: string,
    fromYear: number, fromMonth: number, fromDay: number,
    toYear: number,   toMonth: number,   toDay: number,
  ): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    switch (granularity) {
      case 'year':
        return `date:${fromYear}-01-01..${toYear}-12-31`;
      case 'month': {
        const toLastDay = new Date(toYear, toMonth, 0).getDate();
        return `date:${fromYear}-${pad(fromMonth)}-01..${toYear}-${pad(toMonth)}-${pad(toLastDay)}`;
      }
      case 'day':
        return `date:${fromYear}-${pad(fromMonth)}-${pad(fromDay)}..${toYear}-${pad(toMonth)}-${pad(toDay)}`;
      default:
        return '';
    }
  }

  /** Format a Date as YYYY-MM-DD for the e621 API. */
  formatDateStr(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
}
